/* MeProvider + useMe — agrégat utilisateur/organisation/plan exposé par GET /api/me.
   Retourne l'utilisateur connecté, son organisation active, le plan souscrit et les
   compteurs d'usage mensuels. Alimente le dashboard, les jauges de quota et la Topbar.

   Mutualisé : un seul <MeProvider> (monté dans le shell Layout) effectue le fetch ;
   tous les écrans + la Topbar lisent le même état via useMe() — fini les multiples
   GET /api/me concurrents (un par composant) qu'on avait avant. reload() force un
   rechargement (ex. après changement de plan/profil) sans polling. */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { apiGet } from './api';

export interface Me {
  user: { id: string; name: string; email: string } | null;
  org: {
    id: string;
    name: string;
    planKey: string;
    siren: string | null;
    role: string;
    // Etat du raccordement Plateforme Agreee (Seqino) et du mandat de representation.
    paStatus?: string | null;
    paMandateStatus?: string | null;
    // Date d'activation annoncée par la PA ('YYYY-MM-DD') une fois le KYB validé.
    paMandateStartDate?: string | null;
    mandateSigned?: boolean;
  } | null;
  plan: {
    name: string;
    price_cents: number;
    emitted_quota: number;
    received_quota: number;
    max_seats: number;
    max_external_accountants: number;
  } | null;
  usage: { emitted_count: number; received_count: number };
  /* Limites secondaires « dures » du plan (devis/mois, clients) — quota null = illimité.
     Sert aux bandeaux d'usage ; le blocage réel est appliqué côté serveur (402). */
  limits?: {
    quotes: { used: number; quota: number | null };
    clients: { used: number; quota: number | null };
    catalog: { used: number; quota: number | null };
    templates: { used: number; quota: number | null };
  };
}

type MeState = { me: Me | null; loading: boolean; error: boolean; reload: () => void };

const MeContext = createContext<MeState | null>(null);

/** Fournit l'état /api/me à tout le sous-arbre (monté dans Layout). Un seul fetch. */
export function MeProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    apiGet<Me>('/api/me', ctrl.signal)
      .then((data) => {
        if (!ctrl.signal.aborted) setMe(data);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [tick]);
  const reload = () => setTick((t) => t + 1);
  return <MeContext.Provider value={{ me, loading, error, reload }}>{children}</MeContext.Provider>;
}

/** Lit l'état /api/me partagé. À utiliser sous <MeProvider> (shell Layout). */
export function useMe(): MeState {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error('useMe doit être utilisé dans <MeProvider>');
  return ctx;
}
